import { type Drill, type Path, type Step, type XY } from '../types';
import { getGoalCenter } from './field';
import { buildDribblePath } from './dribblePath';

export interface TimelineState {
  currentTime: number;
  totalDuration: number;
  currentStepIndex: number;
  stepProgress: number;
  actorPositions: Map<string, XY>;
  ballOwner: string | null;
  flyingBall: { from: XY; to: XY; progress: number; via?: XY[] } | null;
}

export class Timeline {
  private drill: Drill;
  private steps: Step[];
  public totalDuration: number;
  public state: TimelineState;

  constructor(drill: Drill) {
    this.drill = drill;
    this.steps = drill.timeline;
    this.totalDuration = this.steps.reduce((sum, step) => sum + step.ms, 0);

    const actorPositions = new Map<string, XY>();
    drill.actors.forEach(actor => {
      if (actor.kind !== 'ball' && actor.kind !== 'area') {
        actorPositions.set(actor.id, { x: actor.x, y: actor.y });
      }
    });

    const ballActor = drill.actors.find(a => a.kind === 'ball');
    const ballOwner = ballActor && 'ownerId' in ballActor ? ballActor.ownerId || null : null;

    this.state = {
      currentTime: 0,
      totalDuration: this.totalDuration,
      currentStepIndex: 0,
      stepProgress: 0,
      actorPositions,
      ballOwner,
      flyingBall: null,
    };
  }

  seek(time: number): void {
    time = Math.max(0, Math.min(time, this.totalDuration));

    let elapsed = 0;
    let stepIndex = 0;

    for (let i = 0; i < this.steps.length; i++) {
      if (elapsed + this.steps[i].ms > time) {
        stepIndex = i;
        break;
      }
      elapsed += this.steps[i].ms;
      stepIndex = i;
    }

    const stepStartTime = this.steps.slice(0, stepIndex).reduce((sum, s) => sum + s.ms, 0);
    const stepProgress = stepIndex < this.steps.length
      ? (time - stepStartTime) / this.steps[stepIndex].ms
      : 1;

    this.state.currentTime = time;
    this.state.currentStepIndex = stepIndex;
    this.state.stepProgress = Math.min(1, Math.max(0, stepProgress));

    this.computeStateAtTime(time);
  }

  private computeStateAtTime(time: number): void {
    const actorPositions = new Map<string, XY>();
    this.drill.actors.forEach(actor => {
      if (actor.kind !== 'ball' && actor.kind !== 'area') {
        actorPositions.set(actor.id, { x: actor.x, y: actor.y });
      }
    });

    const ballActor = this.drill.actors.find(a => a.kind === 'ball');
    let ballOwner = ballActor && 'ownerId' in ballActor ? ballActor.ownerId || null : null;
    let flyingBall: { from: XY; to: XY; progress: number; via?: XY[] } | null = null;

    let elapsed = 0;
    for (const step of this.steps) {
      if (elapsed + step.ms <= time) {
        this.applyStepComplete(step, actorPositions, owner => { ballOwner = owner; });
        elapsed += step.ms;
      } else {
        const progress = step.ms > 0 ? (time - elapsed) / step.ms : 0;
        const result = this.applyStepPartial(step, progress, actorPositions);
        if (result.ballOwner !== undefined) {
          ballOwner = result.ballOwner;
        }
        flyingBall = result.flyingBall;
        break;
      }
    }

    this.state.actorPositions = actorPositions;
    this.state.ballOwner = ballOwner;
    this.state.flyingBall = flyingBall;
  }

  private applyStepComplete(
    step: Step,
    positions: Map<string, XY>,
    setBallOwner: (owner: string | null) => void
  ): void {
    for (const action of step.parallel) {
      if (action.type === 'move' || action.type === 'dribble') {
        const endPos = this.getPathEnd(this.getActionPath(action));
        positions.set(action.actorId, endPos);
      } else if (action.type === 'receive') {
        setBallOwner(action.actorId);
      } else if (action.type === 'pass') {
        setBallOwner(action.toId === 'goal' ? null : action.toId);
      } else if (action.type === 'shoot') {
        setBallOwner(null);
      }
    }
  }

  private applyStepPartial(
    step: Step,
    progress: number,
    positions: Map<string, XY>
  ): { ballOwner?: string | null; flyingBall: { from: XY; to: XY; progress: number; via?: XY[] } | null } {
    let ballOwner: string | null | undefined;
    let flyingBall: { from: XY; to: XY; progress: number; via?: XY[] } | null = null;

    for (const action of step.parallel) {
      if (action.type === 'move' || action.type === 'dribble') {
        const pos = this.interpolatePath(this.getActionPath(action), progress);
        positions.set(action.actorId, pos);
      } else if (action.type === 'pass') {
        const fromPos = positions.get(action.fromId);
        const toPos = action.toId === 'goal' ? getGoalCenter() : positions.get(action.toId);
        if (fromPos && toPos) {
          ballOwner = null;
          flyingBall = {
            from: fromPos,
            to: toPos,
            progress,
            via: action.via,
          };
        }
      } else if (action.type === 'shoot') {
        const fromPos = positions.get(action.fromId);
        if (fromPos) {
          ballOwner = null;
          flyingBall = {
            from: fromPos,
            to: action.target,
            progress,
          };
        }
      } else if (action.type === 'receive') {
        if (progress >= 0.9) {
          ballOwner = action.actorId;
        }
      }
    }

    return { ballOwner, flyingBall };
  }

  private getPathEnd(path: Path): XY {
    if (path.mode === 'poly') {
      return path.pts[path.pts.length - 1];
    } else if (path.mode === 'quad') {
      return path.p2;
    } else if (path.mode === 'spline') {
      return path.pts[path.pts.length - 1];
    }
    return { x: 0, y: 0 };
  }

  private interpolatePath(path: Path, t: number): XY {
    t = Math.max(0, Math.min(1, t));

    if (path.mode === 'poly') {
      const pts = path.pts;
      if (pts.length === 1) return pts[0];

      const totalLen = this.polyLength(pts);
      const targetLen = totalLen * t;

      let accum = 0;
      for (let i = 0; i < pts.length - 1; i++) {
        const segLen = this.distance(pts[i], pts[i + 1]);
        if (accum + segLen >= targetLen) {
          const segT = (targetLen - accum) / segLen;
          return this.lerp(pts[i], pts[i + 1], segT);
        }
        accum += segLen;
      }
      return pts[pts.length - 1];
    } else if (path.mode === 'quad') {
      return this.quadraticBezier(path.p0, path.p1, path.p2, t);
    } else if (path.mode === 'spline') {
      return this.catmullRom(path.pts, t);
    }

    return { x: 0, y: 0 };
  }

  private polyLength(pts: XY[]): number {
    let len = 0;
    for (let i = 0; i < pts.length - 1; i++) {
      len += this.distance(pts[i], pts[i + 1]);
    }
    return len;
  }

  private distance(a: XY, b: XY): number {
    return Math.sqrt((b.x - a.x) ** 2 + (b.y - a.y) ** 2);
  }

  private getActionPath(action: { type: 'move' | 'dribble'; path: Path; wobble?: number }): Path {
    if (action.type === 'dribble') {
      return buildDribblePath(action.path, action.wobble);
    }
    return action.path;
  }

  private lerp(a: XY, b: XY, t: number): XY {
    return {
      x: a.x + (b.x - a.x) * t,
      y: a.y + (b.y - a.y) * t,
    };
  }

  private quadraticBezier(p0: XY, p1: XY, p2: XY, t: number): XY {
    const mt = 1 - t;
    return {
      x: mt * mt * p0.x + 2 * mt * t * p1.x + t * t * p2.x,
      y: mt * mt * p0.y + 2 * mt * t * p1.y + t * t * p2.y,
    };
  }

  private catmullRom(pts: XY[], t: number): XY {
    if (pts.length < 2) return pts[0] || { x: 0, y: 0 };
    if (pts.length === 2) return this.lerp(pts[0], pts[1], t);

    const segCount = pts.length - 1;
    const segT = t * segCount;
    const segIndex = Math.floor(segT);
    const localT = segT - segIndex;

    const i = Math.min(segIndex, pts.length - 2);
    return this.lerp(pts[i], pts[i + 1], localT);
  }

  getCurrentStep(): Step | null {
    if (this.state.currentStepIndex < this.steps.length) {
      return this.steps[this.state.currentStepIndex];
    }
    return null;
  }

  nextStep(): void {
    if (this.state.currentStepIndex < this.steps.length - 1) {
      const nextStepStart = this.steps
        .slice(0, this.state.currentStepIndex + 1)
        .reduce((sum, s) => sum + s.ms, 0);
      this.seek(nextStepStart);
    }
  }

  prevStep(): void {
    if (this.state.currentStepIndex > 0) {
      const prevStepStart = this.steps
        .slice(0, this.state.currentStepIndex)
        .reduce((sum, s) => sum + s.ms, 0);
      this.seek(prevStepStart);
    }
  }
}
